/* sugar:assets/offers.js@2.0.0 */
/*
 * Offers — quantity tiers, per-slot variants, gifts/upsells and live prices.
 *
 * One controller per `[data-sugar-offers]` root (Offer List or Offer Cards). Everything the rest of the theme relies
 * on stays as it was:
 *   - `.offer.is-selected[data-quantity]` marks the live tier
 *   - `data-computed-*` attributes on each card carry the totals
 *   - events: offers:quantity-changed, offers:prices-updated,
 *             offers:lock-selling-plan, variant:change, offers:ready
 *   - window.subscriptionStateBySection[sectionId] is read and written
 *   - root.sugarOffers exposes the controller (selectOffer, setRequestedQuantity,
 *     getRequestedQuantity, selectedVariants, selectedId)
 */
(function () {
  if (window.SugarOffers) return;

  const OFFER_SEL = '.offer';
  const GIFT_SEL = '.sugar-offer-gift, .offer__perk-gift';

  /*
   * Badge geometry (side-by-side edge badges, the all-around ring) lives in deal-badge.js,
   * loaded by the Deal Badge block itself. Auto alignment resolves the way the parent's CSS
   * does: cards centre, a list keeps top badges right and bottom badges left.
   */
  function badgeAutoAlign(offer) {
    const root = offer.closest('[data-sugar-offers]');
    const isList = root && root.dataset.ofLayout === 'list';
    return (badge) => (isList ? (badge.dataset.obPos === 'top' ? 'right' : 'left') : 'center');
  }

  function layoutBadges(offer) {
    if (window.SugarDealBadge) window.SugarDealBadge.layoutCard(offer, badgeAutoAlign(offer));
  }

  function layoutAllEdgeBadges() {
    document.querySelectorAll('[data-sugar-offers] .sugar-offer').forEach(layoutBadges);
  }

  /*
   * The selected card draws its border in from opposite corners. The class has to come back off
   * when it lands: the wedge that reveals the border leaves a hairline seam where its two ends
   * meet, and the settled frame is drawn without it. Timed rather than driven by animationend,
   * which a backgrounded tab does not reliably dispatch — mirrors the 680ms in the stylesheet.
   */
  const BORDER_TRACE_MS = 740;

  function stopTrace(el) {
    if (el._traceTimer) {
      clearTimeout(el._traceTimer);
      el._traceTimer = null;
    }
    el.classList.remove('is-border-tracing');
  }

  function traceBorder(el) {
    if (!el) return;
    stopTrace(el);
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    void el.offsetWidth;
    el.classList.add('is-border-tracing');
    el._traceTimer = setTimeout(() => {
      el._traceTimer = null;
      el.classList.remove('is-border-tracing');
    }, BORDER_TRACE_MS);
  }

  function toCents(value) {
    if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value);
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return 0;
      if (/^-?\d+$/.test(trimmed)) return parseInt(trimmed, 10);
      const normalized = trimmed.replace(/[^0-9.,-]/g, '').replace(/,/g, '');
      const parsed = Number.parseFloat(normalized);
      if (!Number.isFinite(parsed)) return 0;
      return normalized.includes('.') ? Math.round(parsed * 100) : Math.round(parsed);
    }
    return 0;
  }

  function sumCents(values) {
    return values.reduce((sum, value) => sum + toCents(value), 0);
  }

  function normalizeOptionValue(value) {
    return String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();
  }

  function stripAnnotations(str) {
    if (!str) return str;
    return str.replace(/<!--\s*BEGIN setting:.*?-->/g, '').replace(/<!--\s*END setting:.*?-->/g, '').trim();
  }

  function getSubscriptionState(sectionId) {
    return (
      window.subscriptionStateBySection?.[sectionId] ||
      window.subscriptionState ||
      { type: 'onetime', sellingPlan: null, compareBasis: 'onetime', variantComparePrice: 0 }
    );
  }

  function applySubscriptionDiscount(basePrice, sellingPlan) {
    if (!sellingPlan || !sellingPlan.price_adjustments || !sellingPlan.price_adjustments.length) return basePrice;
    const adjustment = sellingPlan.price_adjustments[0];
    if (adjustment.value_type === 'percentage') return Math.round(basePrice * (100 - adjustment.value) / 100);
    if (adjustment.value_type === 'fixed_amount') return Math.max(0, basePrice - adjustment.value);
    if (adjustment.value_type === 'price') return Math.max(0, adjustment.value);
    return basePrice;
  }

  function getFrequencyFromSellingPlan(sellingPlan) {
    let number = '1';
    let unit = '';
    if (!sellingPlan || !Array.isArray(sellingPlan.options)) return { number, unit };
    for (const option of sellingPlan.options) {
      if (!option?.value) continue;
      for (const part of String(option.value).split(' ')) {
        const lower = String(part).toLowerCase();
        if (['month', 'months', 'week', 'weeks', 'day', 'days', 'year', 'years'].includes(lower)) {
          unit = part;
        } else {
          const parsed = parseInt(part, 10);
          if (!Number.isNaN(parsed) && String(parsed) === part && part !== '0') number = part;
        }
      }
    }
    return { number, unit };
  }

  // Discount a list of per-unit prices the way the card's settings describe. A percentage comes
  // off each unit with the part of a cent dropped, which is what checkout charges.
  function applyOfferDiscountToLines(linePrices, discountType, discountValue, discountScope) {
    if (!Array.isArray(linePrices) || linePrices.length === 0) return 0;
    const total = sumCents(linePrices);
    if (discountType === 'percentage' && discountValue > 0) {
      return sumCents(linePrices.map((price) => price - Math.floor((price * Math.round(discountValue * 100)) / 10000)));
    }
    if (discountType === 'fixed' && discountValue > 0) {
      if (discountScope === 'per_item') return sumCents(linePrices.map((price) => Math.max(0, price - discountValue)));
      return Math.max(0, total - discountValue);
    }
    if (discountType === 'set_price' && discountValue > 0) {
      if (discountScope === 'per_item') return Math.round(discountValue * linePrices.length);
      return Math.round(discountValue);
    }
    return total;
  }

  class OffersController {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || '';
      this.sectionEl = document.getElementById(`shopify-section-${this.sectionId}`) || root.closest('.shopify-section');
      this.sectionProductHandle = root.dataset.pageProductHandle || '';
      this.currencyCodeEnabled = root.dataset.currencyCode === 'true';
      // Whole amounts lose their ".00" everywhere the script writes a price
      this.hideCents = root.dataset.ofHideCents === 'true';
      this.borderTraceEnabled = root.dataset.ofBorderAnimate === 'true';

      let variants = [];
      try {
        const json = root.querySelector('script[data-offers-variants]');
        variants = json ? JSON.parse(json.textContent) : [];
      } catch (e) {
        variants = [];
      }
      this.sectionVariants = variants;
      this.sectionVariantsById = new Map(variants.map((variant) => [String(variant.id), variant]));
      this.validVariantIds = new Set(variants.map((variant) => String(variant.id)));
      this.defaultSectionVariant = variants.find((variant) => (parseInt(variant.price, 10) || 0) > 0) || variants[0] || null;

      this.selectedId = root.querySelector(`${OFFER_SEL}.is-selected`)?.dataset.blockId || null;
      this.requestedQuantity = null;
      this.selectedVariants = {};
      this.selectedOptions = {};
      this.combineOptions = true;
      this.removedGifts = {};
      this.addedGiftVariants = new Set();
      this.selectionSideEffectsTimer = null;
      this.lastKnownSellingPlan = null;
      this.lastHandledVariantEventKey = null;
      this._hadExtraUnits = false;

      this.boundHandlers = [];
      root.sugarOffers = this;
      this.init();
    }

    /* ------------------------------------------------------------------ */
    /* Boot                                                                */
    /* ------------------------------------------------------------------ */

    init() {
      window.subscriptionStateBySection = window.subscriptionStateBySection || {};
      if (!window.subscriptionStateBySection[this.sectionId]) {
        window.subscriptionStateBySection[this.sectionId] = {
          type: 'onetime',
          sellingPlan: null,
          compareBasis: 'onetime',
          variantComparePrice: 0
        };
      }

      this.readPickerConfig();
      this.loadRemovedGifts();
      this.setupGiftCartObserver();
      this.bindRootEvents();
      this.bindDocumentEvents();

      const initialOffer = this.getInitialOffer();
      if (initialOffer) {
        // The first announcement restates what the page already shows. Listeners that react
        // to a change (the gallery moving to the variant image) read `initial` and stay put.
        this.initialSelection = true;
        this.selectOffer(initialOffer.dataset.blockId, parseInt(initialOffer.dataset.quantity, 10) || 1, true);
        this.initialSelection = false;
      }
      this.borderTraceReady = true;

      this.setupAnalytics();
      this.setupFormInterception();
      this.refreshPrices();
      // The price blocks have the tier's numbers now (see the tier's --of-pending): let them show.
      if (this.sectionEl) this.sectionEl.style.setProperty('--of-pending', '0');
      this.initLockedTierAvailability();
      this.updateJoinedCardEdges();

      this.root.dataset.offersReady = 'true';
      document.dispatchEvent(new CustomEvent('offers:ready', {
        detail: { sectionId: this.sectionId, blockId: this.root.dataset.sugarEntity || null, root: this.root }
      }));
    }

    destroy() {
      this.boundHandlers.forEach(([target, type, handler, opts]) => target.removeEventListener(type, handler, opts));
      this.boundHandlers = [];
      if (this.cartObserver) this.cartObserver.disconnect();
      if (this.root.sugarOffers === this) delete this.root.sugarOffers;
    }

    listen(target, type, handler, opts) {
      target.addEventListener(type, handler, opts);
      this.boundHandlers.push([target, type, handler, opts]);
    }

    // The Variant Picker child is optional; without it the cards hide their selectors.
    readPickerConfig() {
      const picker = this.root.querySelector('[data-ov]');
      this.picker = picker
        ? {
            type: picker.dataset.ovType || 'dropdown',
            combine: picker.dataset.ovCombine !== 'false'
          }
        : null;
      this.combineOptions = this.picker ? this.picker.combine : true;
    }

    getInitialOffer() {
      const offers = Array.from(this.root.querySelectorAll(OFFER_SEL));
      if (!offers.length) return null;
      const preselected = offers.find((offer) => offer.classList.contains('is-selected'))
        || offers.find((offer) => offer.dataset.preselected === 'true');
      return preselected || offers[0];
    }

    bindRootEvents() {
      // Variant pills — capture so the card's own select handler never sees the click.
      this.listen(this.root, 'click', (e) => {
        const pill = e.target.closest('.offer__variant-pill');
        if (!pill || pill.disabled || !this.root.contains(pill)) return;
        e.preventDefault();
        e.stopPropagation();
        const slot = pill.closest('.offer__variants-slot');
        const offer = pill.closest(OFFER_SEL);
        if (slot && offer) this.selectVariant(offer.dataset.blockId, parseInt(slot.dataset.slot, 10), pill.dataset.variantId);
      }, true);

      this.listen(this.root, 'click', (e) => {
        // Upsell rows toggle their checkbox and never select/deselect the card.
        const upsell = e.target.closest('.sugar-offer-gift[data-gift-type="upsell"]');
        if (upsell && this.root.contains(upsell)) {
          e.stopPropagation();
          this.toggleUpsell(upsell);
          return;
        }
        // Native controls inside the card (selects, links) keep their own behaviour.
        if (e.target.closest('select, a, button, input, label')) return;
        const offer = e.target.closest(OFFER_SEL);
        if (offer && this.root.contains(offer)) {
          this.selectOffer(offer.dataset.blockId, parseInt(offer.dataset.quantity, 10) || 1);
        }
      });

      this.listen(this.root, 'keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const upsell = e.target.closest('.sugar-offer-gift[data-gift-type="upsell"]');
        if (upsell && (e.target === upsell || e.target.classList.contains('sugar-offer-gift__checkbox'))) {
          e.preventDefault();
          e.stopPropagation();
          this.toggleUpsell(upsell);
          return;
        }
        const offer = e.target.closest(OFFER_SEL);
        if (offer && e.target === offer) {
          e.preventDefault();
          this.selectOffer(offer.dataset.blockId, parseInt(offer.dataset.quantity, 10) || 1);
        }
      });

      this.listen(this.root, 'change', (e) => {
        const select = e.target;
        const slot = select.closest('.offer__variants-slot');
        const offer = select.closest(OFFER_SEL);
        if (!slot || !offer) return;
        if (select.classList.contains('offer__variant-select')) {
          this.selectVariant(offer.dataset.blockId, parseInt(slot.dataset.slot, 10), select.value);
        } else if (select.classList.contains('offer__option-select')) {
          this.selectOption(offer.dataset.blockId, parseInt(slot.dataset.slot, 10), parseInt(select.dataset.optionIndex, 10), select.value);
        }
      });
    }

    bindDocumentEvents() {
      this.listen(document, 'variant:change', (e) => this.handleVariantChange(e.detail || {}));
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.handleVariantChange(event?.data || {}));
      }
      this.listen(document, 'subscription:type-changed', (e) => {
        if (e.detail?.sectionId && String(e.detail.sectionId) !== this.sectionId) return;
        const { type, sellingPlan, compareBasis, variantComparePrice, sellingPlanGroupName } = e.detail || {};
        window.subscriptionStateBySection[this.sectionId] = {
          type: type || 'onetime',
          sellingPlan,
          sellingPlanGroupName: sellingPlanGroupName || '',
          compareBasis: compareBasis || 'onetime',
          variantComparePrice: variantComparePrice || 0,
          // A subscription block on the page chose this; the seed above is only a default.
          chosen: true
        };
        window.subscriptionState = window.subscriptionStateBySection[this.sectionId];
        this.updateOfferPrices(type, sellingPlan, compareBasis, variantComparePrice, sellingPlanGroupName);
      });
    }

    /* ------------------------------------------------------------------ */
    /* Scope helpers                                                       */
    /* ------------------------------------------------------------------ */

    getScopeRoot() {
      return this.root.closest('.featured-product-info') || this.sectionEl || this.root;
    }

    getOfferEl(blockId) {
      return this.root.querySelector(`${OFFER_SEL}[data-block-id="${blockId}"]`)
        || this.getScopeRoot()?.querySelector(`${OFFER_SEL}[data-block-id="${blockId}"]`)
        || null;
    }

    getOffers() {
      return Array.from(this.root.querySelectorAll(OFFER_SEL));
    }

    getProductForm() {
      const scope = this.getScopeRoot();
      if (!scope) return null;
      const productFormEl = scope.querySelector('product-form');
      if (productFormEl) {
        const productForm = productFormEl.querySelector('form[data-type="add-to-cart-form"], form');
        if (productForm) return productForm;
      }
      const forms = Array.from(scope.querySelectorAll('form[action*="/cart/add"]'));
      return forms.find((formEl) => formEl.dataset.type === 'add-to-cart-form' || formEl.closest('product-form')) || forms[0] || null;
    }

    getQuantityInput(form) {
      const scope = this.getScopeRoot();
      if (!scope) return null;
      const targetForm = form || this.getProductForm();
      // getAttribute: a form's own <input name="id"> shadows form.id
      const formId = targetForm?.getAttribute('id');
      if (formId) {
        const linked = scope.querySelector(`[name="quantity"][form="${CSS.escape(formId)}"]`);
        if (linked) return linked;
      }
      return (
        scope.querySelector('.product-quantity-selector-block [name="quantity"]') ||
        scope.querySelector('.buy-button-inline-quantity [name="quantity"]') ||
        scope.querySelector('[name="quantity"]')
      );
    }

    getMainVariantFromForm() {
      const variantId = this.getProductForm()?.querySelector('[name="id"]')?.value;
      if (!variantId) return null;
      return this.sectionVariantsById.get(String(variantId)) || null;
    }

    /* ------------------------------------------------------------------ */
    /* Gifts and upsells                                                    */
    /* ------------------------------------------------------------------ */

    getGiftStorageKey() {
      return `sugar_offers_removed_gifts_${window.location.pathname}`;
    }

    loadRemovedGifts() {
      try {
        const stored = localStorage.getItem(this.getGiftStorageKey());
        this.removedGifts = stored ? JSON.parse(stored) : {};
      } catch (e) {
        this.removedGifts = {};
      }
    }

    saveRemovedGifts() {
      try {
        localStorage.setItem(this.getGiftStorageKey(), JSON.stringify(this.removedGifts));
      } catch (e) { /* storage unavailable */ }
    }

    isGiftRemoved(offerId, variantId) {
      return this.removedGifts[`${offerId}_${variantId}`] === true;
    }

    markGiftRemoved(offerId, variantId) {
      this.removedGifts[`${offerId}_${variantId}`] = true;
      this.saveRemovedGifts();
    }

    clearGiftRemoved(offerId, variantId) {
      delete this.removedGifts[`${offerId}_${variantId}`];
      this.saveRemovedGifts();
    }

    setupGiftCartObserver() {
      this.listen(document, 'cart:updated', () => this.checkForRemovedGifts());
      const cartDrawer = document.querySelector('sugar-cart-drawer');
      if (cartDrawer) {
        this.cartObserver = new MutationObserver(() => this.checkForRemovedGifts());
        this.cartObserver.observe(cartDrawer, { childList: true, subtree: true });
      }
    }

    async checkForRemovedGifts() {
      if (!this.addedGiftVariants.size) return;
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        const cartVariantIds = new Set(cart.items.map((item) => String(item.variant_id)));
        for (const giftKey of this.addedGiftVariants) {
          const [offerId, variantId] = giftKey.split('_');
          if (!cartVariantIds.has(variantId)) {
            this.markGiftRemoved(offerId, variantId);
            this.addedGiftVariants.delete(giftKey);
          }
        }
      } catch (e) { /* ignore */ }
    }

    toggleUpsell(giftEl) {
      const checkbox = giftEl.querySelector('.sugar-offer-gift__checkbox');
      const checked = !giftEl.classList.contains('is-checked');
      giftEl.classList.toggle('is-checked', checked);
      if (checkbox) checkbox.setAttribute('aria-checked', String(checked));
      const offer = giftEl.closest(OFFER_SEL);
      const offerId = offer?.dataset.blockId;
      const variantId = giftEl.dataset.variantId;
      if (checked && offerId && variantId) this.clearGiftRemoved(offerId, variantId);
      if (offer && offerId !== this.selectedId) {
        this.selectOffer(offerId, parseInt(offer.dataset.quantity, 10) || 1);
      }
    }

    isUpsellChecked(giftEl) {
      return giftEl.classList.contains('is-checked') || giftEl.querySelector('.sugar-offer-gift__checkbox')?.classList.contains('is-checked');
    }

    hasActiveGifts(offer) {
      if (!offer) return false;
      const offerId = offer.dataset.blockId;
      for (const giftEl of offer.querySelectorAll(GIFT_SEL)) {
        const variantId = giftEl.dataset.variantId;
        if (!variantId || this.isGiftRemoved(offerId, variantId)) continue;
        if (giftEl.dataset.giftType === 'gift') return true;
        if (giftEl.dataset.giftType === 'upsell' && this.isUpsellChecked(giftEl)) return true;
      }
      return false;
    }

    /* ------------------------------------------------------------------ */
    /* Selection                                                            */
    /* ------------------------------------------------------------------ */

    getVariantById(variantId) {
      return this.sectionVariantsById.get(String(variantId)) || null;
    }

    dispatchVariantSelection(variantId, extraDetail) {
      const variant = this.getVariantById(variantId);
      if (!variant) return;
      const detail = { sectionId: this.sectionId, variant };
      if (this.initialSelection) detail.initial = true;
      if (extraDetail && typeof extraDetail === 'object') Object.assign(detail, extraDetail);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.variantChange, { data: detail });
      }
      document.dispatchEvent(new CustomEvent('variant:change', { detail }));
    }

    // Slots after the first never change the product's main variant, so the buy button,
    // price and subscriptions must not hear about them. The media gallery still wants to show
    // the picked variant's image, so it gets its own event. Deferred one tick so it lands
    // after selectOffer's deferred side effects, which re-announce slot 1.
    dispatchSlotMediaChange(variantId, blockId, slot) {
      const variant = this.getVariantById(variantId);
      if (!variant) return;
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('offers:slot-variant-change', {
          detail: { sectionId: this.sectionId, variant, blockId, slot }
        }));
      }, 0);
    }

    isSelected(blockId) {
      return this.selectedId === blockId;
    }

    selectOffer(blockId, quantity, immediate = false, requestedQuantity = null) {
      if (this._analyticsInit && blockId !== this.selectedId) this.trackAnalytics('tier_select', blockId);
      const traceOn = this.borderTraceEnabled && this.borderTraceReady && blockId !== this.selectedId;
      this.selectedId = blockId;

      this.getOffers().forEach((offerEl) => {
        const on = offerEl.dataset.blockId === blockId;
        offerEl.classList.toggle('is-selected', on);
        offerEl.setAttribute('aria-checked', on ? 'true' : 'false');
        if (!on) stopTrace(offerEl);
        if (on) {
          const label = this.root.querySelector('[data-offers-selected]');
          const title = offerEl.querySelector('.offer__title');
          if (label && title) label.textContent = title.textContent.trim();
        }
      });
      if (traceOn) traceBorder(this.getOfferEl(blockId));

      const tierQuantity = Math.max(parseInt(quantity, 10) || 1, 1);
      const requested = parseInt(requestedQuantity, 10);
      this.requestedQuantity = Number.isFinite(requested) && requested > tierQuantity ? requested : tierQuantity;
      if (this.selectionSideEffectsTimer) {
        clearTimeout(this.selectionSideEffectsTimer);
        this.selectionSideEffectsTimer = null;
      }

      if (immediate) {
        this.applyOfferSelectionSideEffects(blockId, quantity);
        return;
      }
      // Paint the selection now; the heavier work runs on the next tick.
      this.selectionSideEffectsTimer = setTimeout(() => {
        this.selectionSideEffectsTimer = null;
        if (this.selectedId !== blockId) return;
        this.applyOfferSelectionSideEffects(blockId, quantity);
      }, 0);
    }

    applyOfferSelectionSideEffects(blockId, quantity) {
      const tierQuantity = Math.max(parseInt(quantity, 10) || 1, 1);
      const requestedQuantity = this.getRequestedQuantity(tierQuantity);
      const extraQuantity = requestedQuantity - tierQuantity;

      this.initVariants(blockId, tierQuantity);
      this.updateAttributionInput(blockId, tierQuantity);

      const form = this.getProductForm();
      const qtyInput = this.getQuantityInput(form);
      if (qtyInput) {
        qtyInput.value = requestedQuantity;
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      document.dispatchEvent(new CustomEvent('offers:quantity-changed', {
        detail: { quantity: tierQuantity, requestedQuantity, extraQuantity, blockId, sectionId: this.sectionId }
      }));

      const offer = this.getOfferEl(blockId);
      const lockedPlanRaw = offer?.dataset.lockedSellingPlanId || '';
      const lockedPlanId = lockedPlanRaw ? parseInt(lockedPlanRaw, 10) : null;
      document.dispatchEvent(new CustomEvent('offers:lock-selling-plan', {
        detail: { sellingPlanId: Number.isFinite(lockedPlanId) ? lockedPlanId : null, blockId, sectionId: this.sectionId }
      }));

      const isLocked = offer?.dataset.variantLocked === 'true';
      const hasCustomProduct = offer?.dataset.productHandle !== this.sectionProductHandle;
      const variantInput = form?.querySelector('[name="id"]');
      const preferredVariantId = this.selectedVariants[blockId]?.[1] || offer?.dataset.variantId;
      if (variantInput && preferredVariantId) {
        if (isLocked || hasCustomProduct) {
          variantInput.value = preferredVariantId;
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.dispatchVariantSelection(preferredVariantId, { source: 'offer', blockId });
      }

      if (extraQuantity > 0 || this._hadExtraUnits) {
        this._hadExtraUnits = extraQuantity > 0;
        this.refreshPrices();
      }
    }

    getRequestedQuantity(tierQuantity) {
      const tier = Math.max(parseInt(tierQuantity, 10) || 1, 1);
      const requested = parseInt(this.requestedQuantity, 10);
      return Number.isFinite(requested) && requested > tier ? requested : tier;
    }

    getQuantityTiers() {
      return this.getOffers()
        .filter((offer) => !offer.classList.contains('offer--tier-unavailable') && offer.dataset.blockId)
        .map((offer) => ({ blockId: offer.dataset.blockId, quantity: Math.max(parseInt(offer.dataset.quantity, 10) || 1, 1) }))
        .sort((a, b) => a.quantity - b.quantity);
    }

    // Free-form quantity from another block (the sticky bar): pick the best tier the
    // quantity covers and keep the leftover units as extras.
    setRequestedQuantity(quantity) {
      let requested = Math.max(parseInt(quantity, 10) || 1, 1);
      const tiers = this.getQuantityTiers();
      if (!tiers.length) {
        this.requestedQuantity = requested;
        return requested;
      }
      let match = null;
      for (const tier of tiers) if (tier.quantity <= requested) match = tier;
      if (!match) {
        match = tiers[0];
        requested = match.quantity;
      }
      const tierChanged = match.blockId !== this.selectedId;
      const quantityChanged = requested !== this.getRequestedQuantity(match.quantity);
      this.requestedQuantity = requested;
      if (tierChanged) this.selectOffer(match.blockId, match.quantity, true, requested);
      else if (quantityChanged) this.applyOfferSelectionSideEffects(match.blockId, match.quantity);
      return requested;
    }

    /* ------------------------------------------------------------------ */
    /* Per-slot variants                                                    */
    /* ------------------------------------------------------------------ */

    initVariants(blockId, quantity) {
      const offer = this.getOfferEl(blockId);
      if (!offer) return;
      const isLocked = offer.dataset.variantLocked === 'true';
      const lockedVariantId = offer.dataset.variantId;
      if (!this.selectedVariants[blockId]) this.selectedVariants[blockId] = {};
      if (!this.selectedOptions[blockId]) this.selectedOptions[blockId] = {};

      for (let slot = 1; slot <= quantity; slot++) {
        if (isLocked && lockedVariantId) {
          this.selectedVariants[blockId][slot] = lockedVariantId;
          continue;
        }
        const container = offer.querySelector(`.offer__variants-slot[data-slot="${slot}"]`);
        if (!this.selectedVariants[blockId][slot]) {
          const dropdown = container?.querySelector('.offer__variant-select');
          const pill = container?.querySelector('.offer__variant-pill.is-active');
          // The shopper's current pick wins over the pill that was active at page load,
          // so choosing a tier never swaps the size or colour they already chose.
          const offerHandle = String(offer.dataset.productHandle || '').trim();
          const isMainProduct = !offerHandle || offerHandle === this.sectionProductHandle;
          const current = isMainProduct ? this.getMainVariantFromForm() : null;
          const value = (current && String(current.id)) || dropdown?.value || pill?.dataset.variantId;
          if (value) this.selectedVariants[blockId][slot] = String(value);
        }
        if (!this.selectedOptions[blockId][slot]) {
          this.selectedOptions[blockId][slot] = [];
          container?.querySelectorAll('.offer__option-select').forEach((select, index) => {
            this.selectedOptions[blockId][slot][index] = select.value;
          });
        }
      }
    }

    findVariantFromOptions(options) {
      return this.sectionVariants.find((variant) => (variant.options || []).every((opt, i) => options[i] === opt));
    }

    selectOption(blockId, slot, optionIndex, value) {
      if (this.selectedId !== blockId) {
        const offer = this.getOfferEl(blockId);
        if (offer) {
          this.selectOffer(blockId, parseInt(offer.dataset.quantity, 10) || 1);
          // Know every slot now, not on the next tick: the form's variant is slot 1's pick.
          this.initVariants(blockId, parseInt(offer.dataset.quantity, 10) || 1);
        }
      }
      if (!this.selectedOptions[blockId]) this.selectedOptions[blockId] = {};
      if (!this.selectedOptions[blockId][slot]) this.selectedOptions[blockId][slot] = [];
      this.selectedOptions[blockId][slot][optionIndex] = value;

      const variant = this.findVariantFromOptions(this.selectedOptions[blockId][slot]);
      if (!variant) return;
      if (!this.selectedVariants[blockId]) this.selectedVariants[blockId] = {};
      this.selectedVariants[blockId][slot] = String(variant.id);
      if (slot === 1) {
        const variantInput = this.getProductForm()?.querySelector('[name="id"]');
        if (variantInput) {
          variantInput.value = variant.id;
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.dispatchVariantSelection(variant.id, { source: 'offer', blockId });
      } else {
        this.dispatchSlotMediaChange(variant.id, blockId, slot);
      }
      this.refreshPrices();
    }

    selectVariant(blockId, slot, variantId) {
      const id = String(variantId);
      if (this.selectedId !== blockId) {
        const offer = this.getOfferEl(blockId);
        if (offer) {
          this.selectOffer(blockId, parseInt(offer.dataset.quantity, 10) || 1);
          // Know every slot now, not on the next tick: the form's variant is slot 1's pick.
          this.initVariants(blockId, parseInt(offer.dataset.quantity, 10) || 1);
        }
      }
      if (!this.selectedVariants[blockId]) this.selectedVariants[blockId] = {};
      this.selectedVariants[blockId][slot] = id;

      const container = this.getOfferEl(blockId)?.querySelector(`.offer__variants-slot[data-slot="${slot}"]`);
      container?.querySelectorAll('.offer__variant-pill').forEach((p) => p.classList.toggle('is-active', p.dataset.variantId === id));
      const dropdown = container?.querySelector('.offer__variant-select');
      if (dropdown && dropdown.value !== id) dropdown.value = id;

      const variantInput = this.getProductForm()?.querySelector('[name="id"]');
      const activeVariantId = this.selectedVariants[blockId]?.[1] || id;
      if (variantInput) {
        variantInput.value = activeVariantId;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (slot === 1) this.dispatchVariantSelection(activeVariantId, { source: 'offer', blockId });
      else this.dispatchSlotMediaChange(id, blockId, slot);
      this.refreshPrices();
    }

    getSelectedVariantForOfferSlot(offer, slot) {
      const offerId = String(offer?.dataset?.blockId || '').trim();
      const stateVariantId = String(this.selectedVariants?.[offerId]?.[slot] || '').trim();
      if (stateVariantId && this.sectionVariantsById.has(stateVariantId)) return this.sectionVariantsById.get(stateVariantId);

      const container = offer?.querySelector(`.offer__variants-slot[data-slot="${slot}"]`);
      const dropdownVariantId = String(container?.querySelector('.offer__variant-select')?.value || '').trim();
      if (dropdownVariantId && this.sectionVariantsById.has(dropdownVariantId)) return this.sectionVariantsById.get(dropdownVariantId);
      const pillVariantId = String(container?.querySelector('.offer__variant-pill.is-active')?.dataset.variantId || '').trim();
      if (pillVariantId && this.sectionVariantsById.has(pillVariantId)) return this.sectionVariantsById.get(pillVariantId);

      // Custom-product offers carry per-variant prices on their own options and pills.
      const domVariantId = stateVariantId || dropdownVariantId || pillVariantId;
      if (domVariantId) {
        const option = container?.querySelector(`.offer__variant-select option[value="${domVariantId}"]`);
        if (option?.dataset.price) return { id: domVariantId, price: option.dataset.price, compare_at_price: option.dataset.comparePrice || 0 };
        const pill = container?.querySelector(`.offer__variant-pill[data-variant-id="${domVariantId}"]`);
        if (pill?.dataset.price) return { id: domVariantId, price: pill.dataset.price, compare_at_price: pill.dataset.comparePrice || 0 };
      }
      return null;
    }

    /* ------------------------------------------------------------------ */
    /* Cart                                                                 */
    /* ------------------------------------------------------------------ */

    setupFormInterception() {
      const form = this.getProductForm();
      if (!form) return;
      // Bound on `window` in the capture phase, not on the form. Some cart apps attach a
      // capture-phase submit listener to `document` and stop propagation there, which runs
      // before anything bound further down the tree and would post a plain single-unit add
      // instead of the selected tier. Window capture runs ahead of that.
      this.listen(window, 'submit', async (e) => {
        const stepForm = this.isStepForm(e.target);
        if (e.target !== form && !stepForm) return;
        // A Step 2's rows below their minimum: the button only says what is missing.
        if (this.bundleShort()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }
        const submitForm = stepForm ? e.target : form;
        const offer = this.getOfferEl(this.selectedId);
        const quantity = parseInt(offer?.dataset.quantity, 10) || 1;
        const isLocked = offer?.dataset.variantLocked === 'true';
        const hasCustomProduct = offer?.dataset.productHandle !== this.sectionProductHandle;
        const hasLockedPlan = !!offer?.dataset.lockedSellingPlanId;
        const hasGiftsToAdd = this.hasActiveGifts(offer);
        const hasExtraUnits = this.getRequestedQuantity(quantity) > Math.max(1, quantity);
        const hasBundle = this.bundleLines().length > 0;

        if (quantity <= 1 && !isLocked && !hasCustomProduct && !hasGiftsToAdd && !hasLockedPlan && !hasExtraUnits && !hasBundle) {
          this.trackAnalytics('atc', `${this.selectedId}:${quantity}`);
          return;
        }

        e.preventDefault();
        e.stopImmediatePropagation();

        const cartExtras = window.SugarCart && window.SugarCart.extras;
        const items = this.buildCartItems(quantity, submitForm);
        if (!items.length) return;
        // A required Personalization field that is empty stops the buy and points at it.
        if (cartExtras && typeof cartExtras.validate === 'function' && !cartExtras.validate(submitForm, items)) return;
        // Lines other blocks queued for this buy (Product Upsells), then what the shopper
        // personalized on the product's own lines
        const extras = cartExtras ? cartExtras.collect(submitForm) : [];
        if (extras.length) items.push(...extras);
        if (cartExtras && typeof cartExtras.decorate === 'function') cartExtras.decorate(submitForm, items);

        const productForm = submitForm.closest('product-form');
        const submitBtn = productForm?.querySelector('[type="submit"]');
        const spinner = productForm?.querySelector('.loading__spinner');
        submitBtn?.setAttribute('aria-disabled', 'true');
        submitBtn?.classList.add('loading');
        spinner?.classList.remove('hidden');

        try {
          const drawer = document.querySelector('sugar-cart-drawer');
          const sections = drawer && typeof drawer.getSectionsToRender === 'function'
            ? drawer.getSectionsToRender().map((section) => section.id)
            : undefined;
          const payload = sections ? { items, sections, sections_url: window.location.pathname } : { items };
          const request = cartExtras && typeof cartExtras.request === 'function'
            ? cartExtras.request(payload)
            : { body: JSON.stringify(payload), contentType: 'application/json' };
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: request.contentType ? { 'Content-Type': request.contentType } : {},
            body: request.body
          });
          const data = await res.json();
          if (data.status) throw new Error(data.description || 'Failed to add items');

          if (extras.length) window.SugarCart.extras.commit(submitForm, data);
          this.trackAnalytics('atc', `${this.selectedId}:${quantity}`);

          // The Buy Button's "Skip cart" goes straight to checkout, whichever path posted the add.
          if (productForm?.dataset.skipCart === 'true') {
            window.location.href = '/checkout';
            return;
          }

          if (drawer) {
            const cartData = window.SugarCart ? await window.SugarCart.get({ fresh: true }) : null;
            if (cartData && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'offers', cartData, sections: data.sections });
            }
            if (typeof drawer.renderContents === 'function') drawer.renderContents(data);
            else drawer.open();
            document.dispatchEvent(new CustomEvent('cart:update', { detail: data }));
          } else {
            window.location.reload();
          }
        } catch (err) {
          const errorEl = productForm?.querySelector('.product-form__error-message');
          const errorWrapper = productForm?.querySelector('.product-form__error-message-wrapper');
          if (errorEl && errorWrapper) {
            errorEl.textContent = err.message || 'Unable to add items to cart.';
            errorWrapper.removeAttribute('hidden');
          }
        } finally {
          submitBtn?.removeAttribute('aria-disabled');
          submitBtn?.classList.remove('loading');
          spinner?.classList.add('hidden');
        }
      }, true);
    }

    /* A Step 2's own Buy Button sells this section's offer too (the rows of its Bundle Builder). */
    isStepForm(target) {
      if (!target || !target.matches || !target.matches('form[action*="/cart/add"]')) return false;
      const step = target.closest('[data-step-two]');
      const scope = this.getScopeRoot();
      return !!(step && scope && scope.contains(step));
    }

    bundleShort() {
      const section = this.root.closest('.shopify-section');
      const bundle = section && section.querySelector('[data-bundle-builder][data-bb-mode="offers"]');
      return !!(bundle && bundle.dataset.bbShort === 'true');
    }

    /* A Bundle Builder in the section, with no tiers of its own, splits the offer's units across its rows. */
    bundleLines() {
      const section = this.root.closest('.shopify-section');
      const bundle = section && section.querySelector('[data-bundle-builder][data-bb-mode="offers"]');
      if (!bundle || !bundle.dataset.bbLines) return [];
      try {
        return JSON.parse(bundle.dataset.bbLines).filter((line) => line && line.id && line.quantity > 0);
      } catch (e) {
        return [];
      }
    }

    buildCartItems(quantity, form) {
      const items = [];
      const offer = this.getOfferEl(this.selectedId);
      const entity = this.root.dataset.sugarEntity || '';
      // The stamp names the block only. A tier or quantity in it would keep Shopify from
      // merging the same product bought through two tiers into one cart line.
      const attribution = entity;
      const giftAttribution = entity;
      const normalizeVariantId = (id) => String(id || '').trim();
      const formVariantId = normalizeVariantId(form?.querySelector('[name="id"]')?.value);
      const offerVariantId = normalizeVariantId(offer?.dataset.variantId);

      const sellingPlanInput = form?.querySelector('input[name="selling_plan"]');
      const formSellingPlanId = (sellingPlanInput && !sellingPlanInput.disabled && sellingPlanInput.value)
        ? parseInt(sellingPlanInput.value, 10)
        : null;
      const lockedSellingPlanRaw = offer?.dataset.lockedSellingPlanId || '';
      const lockedSellingPlanId = lockedSellingPlanRaw ? parseInt(lockedSellingPlanRaw, 10) : null;
      // A shopper who chose one-time on a subscription block has to get one-time, or the
      // purchase silently goes in as a subscription (lockedPlanApplies). The card is priced
      // by the same rule.
      const sellingPlanId = (this.lockedPlanApplies() && Number.isFinite(lockedSellingPlanId)) ? lockedSellingPlanId : formSellingPlanId;

      const offerVariantIds = String(offer?.dataset.offerVariantIds || '').split(',').map(normalizeVariantId).filter(Boolean);
      const offerVariantIdSet = new Set(offerVariantIds);
      const isOfferVariantId = (id) => !!id && (!offerVariantIdSet.size || offerVariantIdSet.has(normalizeVariantId(id)));

      let baseFallbackId = '';
      if (isOfferVariantId(offerVariantId)) baseFallbackId = offerVariantId;
      if (!baseFallbackId && isOfferVariantId(formVariantId)) baseFallbackId = formVariantId;
      if (!baseFallbackId && offerVariantIds[0]) baseFallbackId = offerVariantIds[0];
      if (!baseFallbackId) baseFallbackId = offerVariantId || formVariantId || this.root.dataset.currentVariantId || '';

      const isLocked = offer?.dataset.variantLocked === 'true';
      const lockedVariantId = offerVariantId;
      let baseItemsAdded = 0;

      const bundleLines = this.bundleLines();
      bundleLines.forEach((line) => {
        const bundleItem = { id: parseInt(line.id, 10), quantity: parseInt(line.quantity, 10) };
        if (sellingPlanId) bundleItem.selling_plan = sellingPlanId;
        if (attribution) bundleItem.properties = { _sugar_offer: attribution };
        items.push(bundleItem);
        baseItemsAdded += bundleItem.quantity;
      });

      for (let slot = 1; slot <= (bundleLines.length ? 0 : quantity); slot++) {
        if (isLocked && lockedVariantId) {
          const parsedLockedId = parseInt(lockedVariantId, 10);
          if (Number.isFinite(parsedLockedId)) {
            const lockedItem = { id: parsedLockedId, quantity: 1 };
            if (sellingPlanId) lockedItem.selling_plan = sellingPlanId;
            if (attribution) lockedItem.properties = { _sugar_offer: attribution };
            items.push(lockedItem);
            baseItemsAdded += 1;
          }
          continue;
        }

        let variantId = this.selectedVariants[this.selectedId]?.[slot];
        if (!variantId && offer) {
          const container = offer.querySelector(`.offer__variants-slot[data-slot="${slot}"]`);
          if (this.combineOptions) {
            const dropdown = container?.querySelector('.offer__variant-select');
            const pill = container?.querySelector('.offer__variant-pill.is-active');
            variantId = dropdown?.value || pill?.dataset.variantId;
          } else {
            const optionSelects = container?.querySelectorAll('.offer__option-select');
            if (optionSelects?.length) {
              const variant = this.findVariantFromOptions(Array.from(optionSelects).map((s) => s.value));
              if (variant) variantId = String(variant.id);
            }
          }
        }

        const candidateId = String(variantId || '').trim();
        const resolvedId = (isOfferVariantId(candidateId) ? candidateId : '') || baseFallbackId;
        const parsedResolvedId = parseInt(resolvedId, 10);
        if (!Number.isFinite(parsedResolvedId)) continue;
        const slotItem = { id: parsedResolvedId, quantity: 1 };
        if (sellingPlanId) slotItem.selling_plan = sellingPlanId;
        if (attribution) slotItem.properties = { _sugar_offer: attribution };
        items.push(slotItem);
        baseItemsAdded += 1;
      }

      const extraQuantity = Math.max(this.getRequestedQuantity(quantity) - Math.max(1, quantity), 0);

      if (baseItemsAdded === 0) {
        const parsedFallbackId = parseInt(baseFallbackId, 10);
        if (Number.isFinite(parsedFallbackId)) {
          const fallbackItem = { id: parsedFallbackId, quantity: Math.max(1, quantity) + extraQuantity };
          if (sellingPlanId) fallbackItem.selling_plan = sellingPlanId;
          if (attribution) fallbackItem.properties = { _sugar_offer: attribution };
          items.push(fallbackItem);
        }
      } else if (extraQuantity > 0 && !bundleLines.length) {
        const extraSourceId = (isLocked && lockedVariantId) ? lockedVariantId : (this.selectedVariants[this.selectedId]?.[1] || baseFallbackId);
        const parsedExtraId = parseInt(extraSourceId, 10);
        if (Number.isFinite(parsedExtraId)) {
          const extraItem = { id: parsedExtraId, quantity: extraQuantity };
          if (sellingPlanId) extraItem.selling_plan = sellingPlanId;
          if (attribution) extraItem.properties = { _sugar_offer: attribution };
          items.push(extraItem);
        }
      }

      items.push(...this.giftLines(offer));

      return items;
    }

    // The gift and ticked-upsell lines the selected tier adds. Every buy path that honours a
    // tier (the product form, the text buy button, a sticky bar) reads them from here.
    giftLines(offer = this.getOfferEl(this.selectedId)) {
      const lines = [];
      if (!offer) return lines;
      const entity = this.root.dataset.sugarEntity || '';
      const giftAttribution = entity;
      const qualifyingProductId = offer.dataset.productId || '';
      offer.querySelectorAll(GIFT_SEL).forEach((giftEl) => {
        const giftType = giftEl.dataset.giftType;
        const variantId = giftEl.dataset.variantId;
        const offerId = this.selectedId;
        if (!variantId || this.isGiftRemoved(offerId, variantId)) return;
        if (giftType === 'upsell' && !this.isUpsellChecked(giftEl)) return;
        const parsedGiftId = parseInt(variantId, 10);
        if (!Number.isFinite(parsedGiftId)) return;
        const giftProperties = { _offer_gift: offerId, _offer_gift_type: giftType, _gift_for_product: qualifyingProductId };
        if (giftAttribution) giftProperties._sugar_offer = giftAttribution;
        lines.push({ id: parsedGiftId, quantity: 1, properties: giftProperties });
        this.addedGiftVariants.add(`${offerId}_${variantId}`);
      });
      return lines;
    }

    /* ------------------------------------------------------------------ */
    /* Analytics                                                            */
    /* ------------------------------------------------------------------ */

    analyticsContext() {
      const d = this.root.dataset || {};
      return {
        template: d.sugarTemplate || '',
        path: window.location.pathname,
        product: d.pageProductHandle || '',
        productTitle: d.sugarProductTitle || '',
        label: '',
        section: d.sectionId || ''
      };
    }

    trackAnalytics(type, detail) {
      const entity = this.root.dataset.sugarEntity;
      if (!entity) return;
      const args = [type, 'offers', entity, detail || '', this.analyticsContext()];
      if (window.SugarAnalytics) window.SugarAnalytics.track.apply(null, args);
      else (window._sugarQ = window._sugarQ || []).push(args);
    }

    setupAnalytics() {
      const entity = this.root.dataset.sugarEntity;
      if (!entity) return;
      const ctx = this.analyticsContext();
      if (window.SugarAnalytics) {
        window.SugarAnalytics.observe(this.root, entity, 'offers', ctx);
      } else {
        document.addEventListener('sugar:analytics:ready', () => {
          window.SugarAnalytics.observe(this.root, entity, 'offers', ctx);
        }, { once: true });
      }
      this._analyticsInit = true;
    }

    updateAttributionInput(blockId, quantity) {
      const entity = this.root.dataset.sugarEntity;
      const form = this.getProductForm();
      if (!entity || !form) return;
      let input = form.querySelector('input[name="properties[_sugar_offer]"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[_sugar_offer]';
        form.appendChild(input);
      }
      input.value = entity;
    }

    /* ------------------------------------------------------------------ */
    /* Prices                                                               */
    /* ------------------------------------------------------------------ */

    formatMoney(cents, stripDecimals) {
      if (this.hideCents && Number(cents) % 100 === 0) stripDecimals = true;
      // The shop's own format first, so a price built here matches the Liquid one beside it.
      const shopFormat = this.root.dataset.moneyFormat || '';
      if (shopFormat && window.SugarPricing && typeof window.SugarPricing.formatMoney === 'function') {
        return window.SugarPricing.formatMoney(cents, shopFormat, !!stripDecimals);
      }
      const active = window.Shopify?.currency?.active;
      const locale = window.Shopify?.locale || 'en-US';
      if (stripDecimals) {
        const whole = Math.round(cents / 100);
        if (active) {
          let formatted = new Intl.NumberFormat(locale, { style: 'currency', currency: active, currencyDisplay: 'narrowSymbol', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(whole);
          if (this.currencyCodeEnabled) formatted += ' ' + active;
          return formatted;
        }
        return '$' + whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      }
      const amount = (cents / 100).toFixed(2);
      if (active) {
        let formatted = new Intl.NumberFormat(locale, { style: 'currency', currency: active, currencyDisplay: 'narrowSymbol' }).format(amount);
        if (this.currencyCodeEnabled) formatted += ' ' + active;
        return formatted;
      }
      return '$' + amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    getPlanPlaceholderValues(offer, sellingPlan, sellingPlanGroupName = '') {
      const fallbackPlanName = String(offer?.dataset?.defaultPlanName || '');
      const fallbackNumber = String(offer?.dataset?.defaultFrequencyNumber || '1');
      const fallbackUnit = String(offer?.dataset?.defaultFrequencyUnit || '');
      const eventGroupName = String(sellingPlanGroupName || '');
      if (!sellingPlan) return { planName: eventGroupName || fallbackPlanName, number: fallbackNumber, unit: fallbackUnit };
      const { number, unit } = getFrequencyFromSellingPlan(sellingPlan);
      const groupName = String(sellingPlan.group_name || sellingPlan.groupName || '');
      return {
        planName: groupName || eventGroupName || fallbackPlanName || String(sellingPlan.name || ''),
        number: number || fallbackNumber,
        unit: unit || fallbackUnit
      };
    }

    resolveOfferLinePrices(offer, quantity, fallbackBasePrice, fallbackComparePrice) {
      const safeQuantity = Math.max(quantity, 1);
      const safeBase = Math.max(0, fallbackBasePrice);
      const safeCompare = fallbackComparePrice > 0 ? fallbackComparePrice : safeBase;
      const baseLinePrices = Array(safeQuantity).fill(safeBase);
      const compareLinePrices = Array(safeQuantity).fill(safeCompare);
      for (let slot = 1; slot <= safeQuantity; slot++) {
        const variant = this.getSelectedVariantForOfferSlot(offer, slot);
        if (!variant) continue;
        const slotBasePrice = toCents(variant.price);
        const slotCompareRaw = toCents(variant.compare_at_price);
        baseLinePrices[slot - 1] = Math.max(0, slotBasePrice);
        compareLinePrices[slot - 1] = Math.max(0, slotCompareRaw > 0 ? slotCompareRaw : slotBasePrice);
      }
      return { baseLinePrices, compareLinePrices };
    }

    refreshPrices() {
      const state = getSubscriptionState(this.sectionId);
      this.updateOfferPrices(state.type, state.sellingPlan, state.compareBasis, state.variantComparePrice, state.sellingPlanGroupName);
    }

    /*
     * The plan a tier locks, as the tier prices it. Two pack sizes can each have their own
     * selling plan group with a plan of the same name; each tier is priced on its own
     * variant's plan, never on whichever plan the page happens to hold.
     */
    getOfferOwnPlan(offer) {
      const id = parseInt(offer?.dataset.lockedSellingPlanId, 10);
      const adjustment = offer?.dataset.lockedPlanAdjustment;
      if (!Number.isFinite(id) || !adjustment) return null;
      const split = adjustment.indexOf(':');
      const valueType = adjustment.slice(0, split);
      const value = parseFloat(adjustment.slice(split + 1)) || 0;
      const number = String(offer.dataset.defaultFrequencyNumber || '');
      const unit = String(offer.dataset.defaultFrequencyUnit || '');
      return {
        id,
        name: String(offer.dataset.defaultPlanName || ''),
        group_name: String(offer.dataset.lockedPlanGroup || ''),
        options: [{ name: '', value: (number + ' ' + unit).trim() }],
        price_adjustments: valueType && valueType !== 'none' ? [{ value_type: valueType, value }] : []
      };
    }

    /*
     * Whether a tier's locked plan is what the cart adds. It is, unless the shopper chose
     * one-time on a subscriptions block: with no such block the lock is the only choice there
     * is. Until a block has announced itself, the choice it opens on stands.
     */
    lockedPlanApplies() {
      const state = getSubscriptionState(this.sectionId);
      if (state?.chosen || state?.type === 'subscribe') return state.type !== 'onetime';
      const picker = this.sectionEl?.querySelector('[data-sugar-subscriptions]');
      return !picker || picker.dataset.sbPreselected !== 'onetime';
    }

    updateOfferPrices(type, sellingPlan, compareBasis, variantComparePrice, sellingPlanGroupName = '') {
      if (sellingPlan) this.lastKnownSellingPlan = sellingPlan;
      const pagePlan = sellingPlan || this.lastKnownSellingPlan;
      const fm = (cents, strip) => this.formatMoney(cents, strip);
      const lockApplies = this.lockedPlanApplies();

      this.getOffers().forEach((offer) => {
        // A locked tier is priced on its own plan whenever the cart adds it on that plan.
        const ownPlan = lockApplies ? this.getOfferOwnPlan(offer) : null;
        const activeSellingPlan = ownPlan || pagePlan;
        const onPlan = !!ownPlan || (type === 'subscribe' && !!activeSellingPlan);
        const quantity = Math.max(parseInt(offer.dataset.quantity, 10) || 1, 1);
        let basePrice = toCents(offer.dataset.basePrice);
        const offerVariant = this.sectionVariantsById.get(String(offer.dataset.variantId || ''));
        const offerHandle = String(offer.dataset.productHandle || '').trim();
        const isMainProductOffer = !offerHandle || offerHandle === this.sectionProductHandle;
        const formVariant = isMainProductOffer ? this.getMainVariantFromForm() : null;

        if (basePrice <= 0) {
          let fallbackVariant = null;
          if (offerVariant && toCents(offerVariant.price) > 0) fallbackVariant = offerVariant;
          else if (formVariant && toCents(formVariant.price) > 0) fallbackVariant = formVariant;
          else if (isMainProductOffer && this.defaultSectionVariant) fallbackVariant = this.defaultSectionVariant;
          if (fallbackVariant && toCents(fallbackVariant.price) > 0) {
            basePrice = toCents(fallbackVariant.price);
            offer.dataset.basePrice = String(basePrice);
            if (!offer.dataset.variantId || !this.validVariantIds.has(String(offer.dataset.variantId))) {
              offer.dataset.variantId = String(fallbackVariant.id);
            }
          }
        }

        let discountType = offer.dataset.offerDiscountType;
        let discountValue = parseFloat(offer.dataset.offerDiscountValue) || 0;
        let discountScope = offer.dataset.offerDiscountScope || 'total';
        const pricesContainer = offer.querySelector('.offer__prices');
        const priceTemplate = pricesContainer?.dataset.priceTemplate || '[price]';
        const compareTemplate = pricesContainer?.dataset.compareTemplate || '[compare_price]';

        if (basePrice <= 0 && (type !== 'subscribe' || !sellingPlan)) {
          const serverTotal = toCents(offer.dataset.price);
          if (serverTotal > 0 && quantity > 0) {
            basePrice = Math.round(serverTotal / quantity);
            discountType = null;
            discountValue = 0;
            discountScope = 'total';
            offer.dataset.basePrice = String(basePrice);
          }
        }

        let fallbackCompareBase = toCents(offerVariant?.compare_at_price);
        if (fallbackCompareBase <= 0 && !offerVariant) fallbackCompareBase = toCents(formVariant?.compare_at_price);
        if (fallbackCompareBase <= 0 && !offerVariant && isMainProductOffer && this.defaultSectionVariant) {
          fallbackCompareBase = toCents(this.defaultSectionVariant.compare_at_price);
        }
        if (fallbackCompareBase <= 0 && !isMainProductOffer) fallbackCompareBase = toCents(offer.dataset.baseComparePrice);
        if (fallbackCompareBase <= 0) fallbackCompareBase = basePrice;

        const { baseLinePrices, compareLinePrices } = this.resolveOfferLinePrices(offer, quantity, basePrice, fallbackCompareBase);
        const subscribedLinePrices = onPlan
          ? baseLinePrices.map((price) => applySubscriptionDiscount(price, activeSellingPlan))
          : baseLinePrices;

        const totalPrice = applyOfferDiscountToLines(subscribedLinePrices, discountType, discountValue, discountScope);
        /*
         * The same tier without the page's subscription plan. A button that sells outside the
         * plan — the one-time escape hatch under a subscription-first page — still has to show
         * this tier's price, and only the lines here know how the tier is priced.
         */
        const oneTimeTotalPrice = applyOfferDiscountToLines(baseLinePrices, discountType, discountValue, discountScope);

        const selectedCompareTotal = sumCents(compareLinePrices);
        let originalCompare = selectedCompareTotal > 0 ? selectedCompareTotal : toCents(offer.dataset.comparePrice);
        if (originalCompare <= 0) originalCompare = sumCents(baseLinePrices);
        offer.dataset.comparePrice = String(originalCompare);

        let compareTotalPrice;
        if (onPlan) {
          if (compareBasis === 'maximum' || compareBasis === 'compare_at') {
            compareTotalPrice = selectedCompareTotal > 0 ? selectedCompareTotal : Math.max(0, variantComparePrice * quantity);
            if (compareTotalPrice <= 0) compareTotalPrice = originalCompare;
          } else if (compareBasis === 'before_discounts') {
            compareTotalPrice = sumCents(baseLinePrices);
          } else {
            compareTotalPrice = applyOfferDiscountToLines(baseLinePrices, discountType, discountValue, discountScope);
          }
        } else {
          compareTotalPrice = originalCompare;
        }

        const amountSaved = compareTotalPrice - totalPrice;
        const overallCompare = originalCompare > 0 ? originalCompare : sumCents(baseLinePrices);
        const overallAmountSaved = overallCompare - totalPrice;
        const percentageSaved = overallCompare > 0 && overallAmountSaved > 0 ? Math.round((overallAmountSaved / overallCompare) * 1000) / 10 : 0;
        // [percentage] is the offer's own discount; with no discount it falls back to the overall
        // savings against the compare-at price, the same way the Liquid render does.
        const beforeDiscount = sumCents(subscribedLinePrices);
        const offerPercentage = beforeDiscount > 0 && totalPrice < beforeDiscount
          ? Math.round(((beforeDiscount - totalPrice) / beforeDiscount) * 1000) / 10
          : percentageSaved;

        let subDiscount = '';
        let subDiscountRounded = '';
        let totalPercentage = '';
        let totalPercentageRounded = '';
        let totalSaved = '';
        let totalSavedRounded = '';
        const { planName, number: frequencyNumber, unit: frequencyUnit } = this.getPlanPlaceholderValues(offer, activeSellingPlan, ownPlan ? ownPlan.group_name : sellingPlanGroupName);

        if (activeSellingPlan?.price_adjustments?.length) {
          const adj = activeSellingPlan.price_adjustments[0];
          if (adj.value_type === 'percentage' && adj.value > 0) {
            subDiscount = adj.value + '%';
            subDiscountRounded = Math.round(adj.value) + '%';
          } else if ((adj.value_type === 'fixed_amount' || adj.value_type === 'price') && adj.value > 0) {
            subDiscount = fm(adj.value);
            subDiscountRounded = fm(Math.ceil(adj.value / 100) * 100);
          }
        }

        if (overallAmountSaved > 0 && overallCompare > 0) {
          const totalPct = (overallAmountSaved / overallCompare) * 100;
          totalPercentage = totalPct.toFixed(1).replace(/\.0$/, '') + '%';
          totalPercentageRounded = Math.round(totalPct) + '%';
          totalSaved = fm(overallAmountSaved);
          totalSavedRounded = fm(Math.ceil(overallAmountSaved / 100) * 100, true);
        }

        const setAll = (sel, text) => offer.querySelectorAll(sel).forEach((el) => { el.textContent = text; });
        setAll('[data-offer-sub-discount]', subDiscount);
        setAll('[data-offer-sub-discount-rounded]', subDiscountRounded);
        setAll('[data-offer-total-percentage]', totalPercentage);
        setAll('[data-offer-total-percentage-rounded]', totalPercentageRounded);
        setAll('[data-offer-total-saved]', totalSaved);
        setAll('[data-offer-total-saved-rounded]', totalSavedRounded);
        setAll('[data-offer-plan-name]', planName);
        setAll('[data-offer-frequency-number]', frequencyNumber);
        setAll('[data-offer-frequency-unit]', frequencyUnit);

        const priceEach = Math.floor(totalPrice / quantity);
        const compareEach = Math.floor(compareTotalPrice / quantity);
        setAll('[data-offer-price-each]', fm(priceEach));
        setAll('[data-offer-compare-each]', fm(compareEach));
        // The title's and subtitle's amounts: the same numbers the price line and badges get.
        const tokenValues = {
          price: fm(totalPrice),
          compare_price: fm(compareTotalPrice),
          percentage: offerPercentage + '%',
          percentage_rounded: Math.round(offerPercentage) + '%',
          amount: fm(overallAmountSaved),
          amount_rounded: fm(Math.round(overallAmountSaved / 100) * 100, true)
        };
        offer.querySelectorAll('[data-offer-token]').forEach((el) => {
          const value = tokenValues[el.dataset.offerToken];
          if (value !== undefined) el.textContent = value;
        });
        offer.querySelectorAll('[data-offer-price-per]').forEach((el) => {
          const n = parseInt(el.dataset.offerPricePer, 10);
          if (n > 0) el.textContent = fm(Math.round(totalPrice / n));
        });

        const substitute = (tpl) => String(tpl || '')
          .replace(/\[price\]/g, fm(totalPrice))
          .replace(/\[price_per_(\d+)\]/g, (m, n) => { n = parseInt(n, 10); return n > 0 ? fm(Math.round(totalPrice / n)) : m; })
          .replace(/\[price_each\]/g, fm(priceEach))
          .replace(/\[compare_price\]/g, fm(compareTotalPrice))
          .replace(/\[compare_each\]/g, fm(compareEach))
          .replace(/\[percentage\]/g, offerPercentage + '%')
          .replace(/\[percentage_rounded\]/g, Math.round(offerPercentage) + '%')
          .replace(/\[amount_saved\]/g, fm(overallAmountSaved))
          .replace(/\[amount\]/g, fm(overallAmountSaved))
          .replace(/\[amount_rounded\]/g, fm(Math.round(overallAmountSaved / 100) * 100, true))
          .replace(/\[quantity\]/g, quantity)
          .replace(/\[total_percentage\]/g, totalPercentage)
          .replace(/\[total_percentage_rounded\]/g, totalPercentageRounded)
          .replace(/\[total_saved\]/g, totalSaved)
          .replace(/\[total_saved_rounded\]/g, totalSavedRounded)
          .replace(/\[sub_discount\]/g, subDiscount)
          .replace(/\[sub_discount_rounded\]/g, subDiscountRounded)
          .replace(/\[plan_name\]/g, planName)
          .replace(/\[number\]/g, frequencyNumber)
          .replace(/\[unit\]/g, frequencyUnit)
          .replace(/\[br\]/g, '<br>');

        if (pricesContainer) {
          const priceEl = pricesContainer.querySelector('.offer__price');
          if (priceEl) priceEl.innerHTML = substitute(priceTemplate);
          pricesContainer.querySelectorAll('.offer__compare-price').forEach((el) => {
            if (amountSaved > 0) {
              el.innerHTML = substitute(compareTemplate);
              el.style.display = '';
            } else {
              el.style.display = 'none';
            }
          });
          pricesContainer.dataset.hasSavings = amountSaved > 0 ? amountSaved : 0;
        }

        // Badges and perks are child blocks: their tokens arrive as templates and are filled here.
        offer.querySelectorAll('[data-badge-template]').forEach((el) => {
          el.innerHTML = substitute(el.dataset.badgeTemplate);
          el.removeAttribute('data-badge-pending');
          const badge = el.closest('.sugar-deal-badge');
          if (badge) badge.removeAttribute('data-badge-pending');
        });
        layoutBadges(offer);
        offer.querySelectorAll('[data-perk-template]').forEach((el) => { el.innerHTML = substitute(el.dataset.perkTemplate); });

        offer.dataset.computedTotalPrice = String(totalPrice);
        offer.dataset.computedComparePrice = String(compareTotalPrice);
        offer.dataset.computedQuantity = String(quantity);
        offer.dataset.computedOneTimeTotalPrice = String(oneTimeTotalPrice);
        offer.dataset.computedOneTimeComparePrice = String(originalCompare);

        const isSelectedOffer = offer.classList.contains('is-selected');
        const requestedQuantity = isSelectedOffer ? this.getRequestedQuantity(quantity) : quantity;
        const extraQuantity = Math.max(requestedQuantity - quantity, 0);
        const extraUnitPrice = subscribedLinePrices[subscribedLinePrices.length - 1] || basePrice;
        const extraUnitCompare = compareLinePrices[compareLinePrices.length - 1] || extraUnitPrice;
        const requestedTotalPrice = totalPrice + (extraQuantity * extraUnitPrice);
        const requestedComparePrice = compareTotalPrice + (extraQuantity * extraUnitCompare);
        const oneTimeExtraUnit = baseLinePrices[baseLinePrices.length - 1] || basePrice;
        offer.dataset.computedRequestedQuantity = String(requestedQuantity);
        offer.dataset.computedRequestedTotalPrice = String(requestedTotalPrice);
        offer.dataset.computedRequestedComparePrice = String(requestedComparePrice);
        offer.dataset.computedRequestedOneTimeTotalPrice = String(oneTimeTotalPrice + extraQuantity * oneTimeExtraUnit);
        offer.dataset.computedRequestedOneTimeComparePrice = String(originalCompare + extraQuantity * extraUnitCompare);

        if (isSelectedOffer) {
          document.dispatchEvent(new CustomEvent('offers:prices-updated', {
            detail: {
              sectionId: this.sectionId,
              blockId: offer.dataset.blockId || null,
              quantity,
              totalPrice,
              comparePrice: compareTotalPrice,
              requestedQuantity,
              extraQuantity,
              requestedTotalPrice,
              requestedComparePrice
            }
          }));
        }
      });
    }

    /* ------------------------------------------------------------------ */
    /* Variant changes from outside                                         */
    /* ------------------------------------------------------------------ */

    resolveLockedOptionVariant(offer, pickedOptions) {
      const lockIdx = parseInt(offer.dataset.lockedOptionIndex, 10);
      const lockValue = normalizeOptionValue(offer.dataset.lockedOptionValue);
      if (!Number.isFinite(lockIdx) || !lockValue || !pickedOptions.length) return undefined;
      return this.sectionVariants.find((candidate) => {
        const candidateOptions = candidate.options || [];
        if (normalizeOptionValue(candidateOptions[lockIdx]) !== lockValue) return false;
        return candidateOptions.every((opt, i) => i === lockIdx || opt === pickedOptions[i]);
      }) || null;
    }

    handleVariantChange(detail) {
      if (!detail?.variant) return;
      if (detail.sectionId && String(detail.sectionId) !== this.sectionId) return;
      if (!this.validVariantIds.has(String(detail.variant.id))) return;

      const eventKey = [detail.sectionId || this.sectionId, detail.variant.id, detail.variant.price, detail.variant.compare_at_price || 0].join(':');
      if (eventKey === this.lastHandledVariantEventKey) return;
      this.lastHandledVariantEventKey = eventKey;

      const variantPrice = toCents(detail.variant.price);
      const variantComparePrice = toCents(detail.variant.compare_at_price);
      const mappedVariant = this.sectionVariantsById.get(String(detail.variant.id));
      const resolvedVariantPrice = variantPrice > 0 ? variantPrice : toCents(mappedVariant?.price);
      const resolvedVariantCompare = variantComparePrice > 0 ? variantComparePrice : toCents(mappedVariant?.compare_at_price);
      if (resolvedVariantPrice <= 0) return;

      // A change made on one card must not overwrite the other cards' own picks.
      if (detail.source !== 'offer') {
        this.getOffers().forEach((offer) => {
          const offerHandle = String(offer.dataset.productHandle || '').trim();
          const isMainProductOffer = !offerHandle || offerHandle === this.sectionProductHandle;
          const isLockedVariant = offer.dataset.variantLocked === 'true';

          if (isMainProductOffer && isLockedVariant && offer.dataset.lockedOptionIndex !== undefined) {
            const pickedVariant = this.sectionVariantsById.get(String(detail.variant.id));
            const pickedOptions = pickedVariant?.options || detail.variant.options || [];
            const resolved = this.resolveLockedOptionVariant(offer, pickedOptions);
            if (resolved === undefined) return;
            this.setOfferTierAvailability(offer, !!resolved);
            if (resolved && String(resolved.id) !== String(offer.dataset.variantId)) {
              const lockedQuantity = parseInt(offer.dataset.quantity, 10) || 1;
              const resolvedPrice = toCents(resolved.price);
              const resolvedCompare = toCents(resolved.compare_at_price) > 0 ? toCents(resolved.compare_at_price) : resolvedPrice;
              offer.dataset.variantId = String(resolved.id);
              offer.dataset.basePrice = String(resolvedPrice);
              offer.dataset.baseComparePrice = String(resolvedCompare);
              offer.dataset.comparePrice = String(resolvedCompare * lockedQuantity);
              const lockedOfferId = offer.dataset.blockId;
              if (lockedOfferId) {
                if (!this.selectedVariants[lockedOfferId]) this.selectedVariants[lockedOfferId] = {};
                for (let s = 1; s <= lockedQuantity; s++) this.selectedVariants[lockedOfferId][s] = String(resolved.id);
              }
              if (this.selectedId === offer.dataset.blockId) {
                const lockedVariantInput = this.getProductForm()?.querySelector('input[name="id"]');
                if (lockedVariantInput) lockedVariantInput.value = String(resolved.id);
              }
              if (resolved.featured_image?.src) {
                offer.querySelectorAll('.offer__image--product img').forEach((img) => {
                  img.removeAttribute('srcset');
                  img.src = resolved.featured_image.src;
                });
              }
            }
            return;
          }

          if (!isMainProductOffer || isLockedVariant) return;

          const quantity = parseInt(offer.dataset.quantity, 10) || 1;
          const compareBase = resolvedVariantCompare > 0 ? resolvedVariantCompare : resolvedVariantPrice;
          offer.dataset.basePrice = String(resolvedVariantPrice);
          offer.dataset.comparePrice = String(compareBase * quantity);
          offer.dataset.variantId = String(detail.variant.id);

          const offerId = offer.dataset.blockId;
          if (offerId && this.selectedVariants[offerId]) {
            for (let s = 1; s <= quantity; s++) {
              if (this.selectedVariants[offerId][s]) this.selectedVariants[offerId][s] = String(detail.variant.id);
            }
          }
        });

        this.root.querySelectorAll('.offer__variant-select').forEach((sel) => {
          const offer = sel.closest(OFFER_SEL);
          if (!offer || offer.dataset.variantLocked === 'true') return;
          const offerHandle = String(offer.dataset.productHandle || '').trim();
          if (offerHandle && offerHandle !== this.sectionProductHandle) return;
          if (sel.querySelector(`option[value="${detail.variant.id}"]`)) sel.value = String(detail.variant.id);
        });
        this.root.querySelectorAll('.offer__variants-slot').forEach((slot) => {
          const offer = slot.closest(OFFER_SEL);
          if (!offer || offer.dataset.variantLocked === 'true') return;
          const offerHandle = String(offer.dataset.productHandle || '').trim();
          if (offerHandle && offerHandle !== this.sectionProductHandle) return;
          slot.querySelectorAll('.offer__variant-pill').forEach((pill) => {
            pill.classList.toggle('is-active', pill.dataset.variantId === String(detail.variant.id));
          });
        });
      }

      this.refreshPrices();
    }

    // A tier that does not exist for the picked options (a pack size not sold in this
    // colour) hides rather than pointing at a stale variant.
    setOfferTierAvailability(offer, available) {
      const wasHidden = offer.classList.contains('offer--tier-unavailable');
      if (available) {
        if (wasHidden) {
          offer.classList.remove('offer--tier-unavailable');
          offer.style.removeProperty('display');
          this.updateJoinedCardEdges();
        }
        return;
      }
      if (wasHidden) return;
      offer.classList.add('offer--tier-unavailable');
      offer.style.setProperty('display', 'none', 'important');
      this.updateJoinedCardEdges();
      if (this.selectedId === offer.dataset.blockId) {
        const next = this.getOffers().find((candidate) => candidate !== offer && !candidate.classList.contains('offer--tier-unavailable'));
        if (next) this.selectOffer(next.dataset.blockId, parseInt(next.dataset.quantity, 10) || 1, true);
      }
    }

    updateJoinedCardEdges() {
      if (this.root.dataset.ofStyle !== 'joined' || !this.root.classList.contains('sugar-offer-cards')) return;
      const offers = this.getOffers().filter((offer) => !offer.classList.contains('offer--tier-unavailable'));
      const count = offers.length;
      for (const suffix of ['d', 'm']) {
        const scrolling = suffix === 'm' && this.root.dataset.ofScroll === 'true';
        const columns = scrolling ? count : Math.max(1, Math.min(count, parseInt(this.root.style.getPropertyValue(`--of-cols-${suffix}`), 10) || (suffix === 'd' ? 3 : 2)));
        offers.forEach((offer, index) => {
          const rowStart = Math.floor(index / columns) * columns;
          const rowCount = Math.min(columns, count - rowStart);
          const column = index - rowStart;
          const edges = [];
          if (rowStart === 0) edges.push('top');
          if (rowStart + rowCount === count) edges.push('bottom');
          if (column === 0) edges.push('left');
          if (column === rowCount - 1) edges.push('right');
          for (const [name, value] of [
            [`data-of-joined-${suffix}`, edges.join(' ')],
            [`data-of-joined-span-${suffix}`, String(scrolling ? 12 : 12 / rowCount)]
          ]) {
            if (offer.getAttribute(name) !== value) offer.setAttribute(name, value);
          }
        });
      }
    }

    initLockedTierAvailability() {
      const currentId = String(this.getProductForm()?.querySelector('input[name="id"]')?.value || '');
      const pickedOptions = this.sectionVariantsById.get(currentId)?.options || [];
      if (!pickedOptions.length) return;
      this.getOffers().forEach((offer) => {
        if (offer.dataset.variantLocked !== 'true') return;
        const offerHandle = String(offer.dataset.productHandle || '').trim();
        if (offerHandle && offerHandle !== this.sectionProductHandle) return;
        const resolved = this.resolveLockedOptionVariant(offer, pickedOptions);
        if (resolved === undefined) return;
        this.setOfferTierAvailability(offer, !!resolved);
      });
    }
  }

  /* -------------------------------------------------------------------- */
  /* Registry                                                               */
  /* -------------------------------------------------------------------- */

  function init(root) {
    if (!root || root.sugarOffers) return root?.sugarOffers || null;
    return new OffersController(root);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-sugar-offers][data-section-id]').forEach(init);
  }

  window.SugarOffers = {
    init,
    initAll,
    get(el) {
      const root = el?.closest?.('[data-sugar-offers]') || el;
      return root?.sugarOffers || null;
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Editor re-renders: tear down the old controller on the removed root, boot the new one.
  let badgeResizeTimer = 0;
  window.addEventListener('resize', () => {
    clearTimeout(badgeResizeTimer);
    badgeResizeTimer = setTimeout(layoutAllEdgeBadges, 100);
  });
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target?.querySelectorAll?.('[data-sugar-offers]').forEach((root) => root.sugarOffers?.destroy());
  });
  document.addEventListener('shopify:block:select', (e) => {
    const root = e.target?.closest?.('[data-sugar-offers]');
    const controller = root?.sugarOffers;
    const offer = e.target?.closest?.(OFFER_SEL);
    if (controller && offer && root.contains(offer)) {
      controller.selectOffer(offer.dataset.blockId, parseInt(offer.dataset.quantity, 10) || 1, true);
    }
  });
})();
